import React, { useLayoutEffect, useRef, useState } from "react";
import {
  formatPlAmount,
  chartDomainWithZero,
  amountToPlotBottomPx,
  layoutTrendBarFromZero,
} from "../../utils/formatPlAmount";

const MIN_PLOT_HEIGHT_PX = 200;
const Y_AXIS_PX_PER_TICK = 48;
const Y_AXIS_MIN_TICKS = 3;
const Y_AXIS_MAX_TICKS = 7;

export interface TwelveMonthRevenueChartProps {
  labels: string[];
  values: number[];
  /** Kept for API compatibility; bars render with a single fixed color. */
  barVariants?: ("normal" | "strong" | "current")[];
  /** Precomputed average for the line (from API). */
  average?: number;
  /** Label for average in legend (e.g. "Avg ₹12.5L"). */
  averageLabel?: string;
  /** Dynamic subtitle; if not provided, derived from labels (e.g. "Jan–Nov 2025") */
  rangeLabel?: string;
  loading?: boolean;
}

const FIXED_BAR_CLASS = "bg-[#86efac]";

export function TwelveMonthRevenueChart({
  labels,
  values,
  average,
  averageLabel,
  rangeLabel,
  loading = false,
}: TwelveMonthRevenueChartProps) {
  const [hoveredBarIndex, setHoveredBarIndex] = useState<number | null>(null);
  const plotRef = useRef<HTMLDivElement | null>(null);
  const [plotHeightPx, setPlotHeightPx] = useState<number>(MIN_PLOT_HEIGHT_PX);

  useLayoutEffect(() => {
    const el = plotRef.current;
    if (!el) return;

    const update = () => {
      const next = Math.max(MIN_PLOT_HEIGHT_PX, Math.floor(el.clientHeight));
      setPlotHeightPx((prev) => (prev === next ? prev : next));
    };

    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { yMin, yMax } = chartDomainWithZero(values);
  const span = Math.max(yMax - yMin, 1);
  const computedAverage: number =
    average !== undefined && average !== null
      ? average
      : values.length > 0
        ? values.reduce((sum, v) => sum + v, 0) / values.length
        : 0;
  const averageLineBottomPx = amountToPlotBottomPx(
    computedAverage,
    yMin,
    yMax,
    plotHeightPx,
  );
  const showAverageLine =
    span > 0 &&
    computedAverage >= yMin &&
    computedAverage <= yMax;

  const chartSubtitle =
    rangeLabel ||
    (labels.length >= 2
      ? `${labels[0]}–${labels[labels.length - 1]} ${new Date().getFullYear()}`
      : `Jan–Dec ${new Date().getFullYear()}`);

  const displayedAverageLabel =
    averageLabel ?? `Avg ${formatPlAmount(computedAverage)}`;

  const desiredTickCount = Math.floor(plotHeightPx / Y_AXIS_PX_PER_TICK) + 1;
  const yAxisTickCount = Math.max(
    Y_AXIS_MIN_TICKS,
    Math.min(Y_AXIS_MAX_TICKS, desiredTickCount),
  );
  const yAxisTickValues = Array.from({ length: yAxisTickCount }).map((_, i) => {
    if (yAxisTickCount <= 1) return yMax;
    const t = i / (yAxisTickCount - 1);
    return yMin + (1 - t) * (yMax - yMin);
  });
  const showInteriorZeroLine = yMin < 0 && yMax > 0;

  if (loading) {
    return (
      <div className="flex h-full min-h-[240px] min-w-0 w-full flex-[2] flex-col items-start gap-6 self-stretch px-6 py-6">
        <div className="h-4 w-64 animate-pulse rounded bg-neutral-200" />
        <div className="flex min-h-0 flex-1 w-full items-end justify-between gap-2">
          {Array.from({ length: 12 }).map((_, i) => (
            <div
              key={i}
              className="flex-1 max-w-[24px] rounded-t bg-neutral-200 animate-pulse"
              style={{ height: `${30 + Math.random() * 60}px` }}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-w-0 flex-[2] flex-col items-start gap-6 self-stretch px-6 py-6">
      <div className="flex w-full items-center justify-between">
        <span className="text-caption-bold font-caption-bold text-neutral-400 uppercase tracking-wider">
          Monthly Revenue · {chartSubtitle}
        </span>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="flex w-6 flex-none items-start border-t-2 border-dashed border-neutral-400" />
            <span className="text-caption font-caption text-neutral-500">
              {displayedAverageLabel}
            </span>
          </div>
        </div>
      </div>
      <div className="flex w-full items-stretch gap-2 flex-1 min-h-0">
        <div
          className="relative shrink-0"
          style={{ height: `${plotHeightPx}px`, width: "3.25rem" }}
        >
          {yAxisTickValues.map((tickValue, i) => {
            const bottomPx = amountToPlotBottomPx(
              tickValue,
              yMin,
              yMax,
              plotHeightPx,
            );
            return (
              <span
                key={i}
                className="absolute right-0 text-caption font-caption text-neutral-400 leading-none"
                style={{
                  bottom: bottomPx,
                  transform: "translateY(50%)",
                }}
              >
                {formatPlAmount(tickValue)}
              </span>
            );
          })}
        </div>
        <div className="flex min-w-0 grow flex-col gap-2 flex-1 min-h-0">
          <div
            ref={plotRef}
            className="relative w-full flex-1 min-h-[220px]"
          >
            <div className="absolute inset-x-2 inset-y-0">
              {showInteriorZeroLine && (
                <div
                  className="pointer-events-none absolute inset-x-0 z-[5] border-t border-solid border-neutral-200"
                  style={{
                    bottom: amountToPlotBottomPx(
                      0,
                      yMin,
                      yMax,
                      plotHeightPx,
                    ),
                  }}
                  aria-hidden
                />
              )}
              {showAverageLine && (
                <div
                  className="pointer-events-none absolute inset-x-0 z-[5] border-t-2 border-dashed border-neutral-400"
                  style={{ bottom: averageLineBottomPx }}
                  aria-hidden
                />
              )}
              <div className="absolute inset-0 z-10 flex justify-between gap-0.5">
              {labels.map((label, i) => {
                const amount = formatPlAmount(values[i]);
                const isHovered = hoveredBarIndex === i;
                const val = values[i] ?? 0;
                const { bottomPx, heightPx, roundedEnd } =
                  layoutTrendBarFromZero(
                    val,
                    yMin,
                    yMax,
                    plotHeightPx,
                  );
                const radiusClass =
                  roundedEnd === "top"
                    ? "rounded-t-md rounded-b-none"
                    : "rounded-b-md rounded-t-none";
                return (
                  <div
                    key={i}
                    className="relative flex h-full min-w-0 flex-1 cursor-default flex-col items-center"
                    onMouseEnter={() => setHoveredBarIndex(i)}
                    onMouseLeave={() => setHoveredBarIndex(null)}
                  >
                    {isHovered && (
                      <div
                        className="absolute bottom-full left-1/2 z-20 mb-1 -translate-x-1/2 whitespace-nowrap rounded-md border border-neutral-200 bg-white px-2 py-1.5 shadow-md"
                        role="tooltip"
                      >
                        <span className="text-caption-bold font-caption-bold text-neutral-900">
                          {label}
                          {amount !== "—" && ` · ${amount}`}
                        </span>
                      </div>
                    )}
                    <div
                      className={`pointer-events-none absolute left-1/2 w-full max-w-[24px] -translate-x-1/2 ${FIXED_BAR_CLASS} ${radiusClass} ${isHovered ? "opacity-90" : ""}`}
                      style={{
                        bottom: bottomPx,
                        height: heightPx,
                      }}
                    />
                  </div>
                );
              })}
              </div>
            </div>
          </div>
          <div className="flex w-full justify-between gap-0.5 px-2 flex-none">
            {labels.map((label, i) => (
              <span
                key={i}
                className="text-caption font-caption flex-1 min-w-0 truncate text-center text-neutral-400"
              >
                {label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
